/** Small shared pieces of the desk UI. */
import { cx } from '@hopex/ui';
import { Globe2, MessageCircle, ShieldAlert, ShieldCheck, Ticket, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { KIND_FA, PRIORITY_FA } from '../lib/labels.ts';
import type { ThreadKind } from '../lib/types.ts';

/** Re-renders every `ms` (live «waiting for 2:14» counters). */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms]);
  return now;
}

const fa = (n: number) => n.toLocaleString('fa-IR');
/** «۴۵ ثانیه»، «۳ دقیقه»، «۲ ساعت»، «۳ روز». */
export function span(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${fa(s)} ثانیه`;
  const m = Math.round(s / 60);
  if (m < 60) return `${fa(m)} دقیقه`;
  const h = Math.round(m / 60);
  if (h < 48) return `${fa(h)} ساعت`;
  return `${fa(Math.round(h / 24))} روز`;
}

export function KindBadge({ kind, className }: { kind: ThreadKind; className?: string }) {
  const Icon = kind === 'TICKET' ? Ticket : kind === 'CHAT_SITE' ? Globe2 : MessageCircle;
  return (
    <span
      className={cx(
        'inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2 text-[11px] font-bold',
        kind === 'TICKET'
          ? 'bg-surface-3 text-fg-muted'
          : kind === 'CHAT_SITE'
            ? 'bg-[color-mix(in_srgb,var(--hx-info)_16%,transparent)] text-info'
            : 'bg-accent-tint text-accent-text',
        className,
      )}
      data-kind={kind}
    >
      <Icon aria-hidden className="size-3.5" />
      {KIND_FA[kind]}
    </span>
  );
}

export function TrustBadge({ verified, guest }: { verified: boolean; guest: boolean }) {
  if (verified)
    return (
      <span
        className="inline-flex h-6 items-center gap-1 rounded-full bg-positive-tint px-2 text-[11px] font-bold text-positive"
        data-trust="verified"
      >
        <ShieldCheck aria-hidden className="size-3.5" />
        کاربر تأییدشده
      </span>
    );
  return (
    <span
      className="inline-flex h-6 items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--hx-pending)_18%,transparent)] px-2 text-[11px] font-bold text-pending"
      data-trust="unverified"
    >
      {guest ? (
        <UserRound aria-hidden className="size-3.5" />
      ) : (
        <ShieldAlert aria-hidden className="size-3.5" />
      )}
      شماره تأییدنشده
    </span>
  );
}

export function PriorityChip({ priority }: { priority: string }) {
  if (priority === 'NORMAL' || priority === 'LOW') return null;
  return (
    <span
      className={cx(
        'inline-flex h-6 items-center rounded-full px-2 text-[11px] font-bold',
        priority === 'URGENT' ? 'bg-negative text-white' : 'bg-negative-tint text-negative',
      )}
      data-priority={priority}
    >
      {PRIORITY_FA[priority] ?? priority}
    </span>
  );
}

/** Circular countdown for an offer (server `expiresAt`; never a client-made deadline). */
export function CountdownRing({
  expiresAt,
  total = 45,
  size = 44,
}: {
  expiresAt: string;
  total?: number;
  size?: number;
}) {
  const now = useNow(1000);
  const left = Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 1000));
  const r = size / 2 - 3;
  const c = 2 * Math.PI * r;
  const frac = Math.min(1, left / Math.max(total, left, 1));
  return (
    <span
      className="relative inline-grid shrink-0 place-items-center"
      style={{ width: size, height: size }}
      aria-label={`${fa(left)} ثانیه برای پذیرش`}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--hx-line)"
          strokeWidth={3}
        />
        <circle
          className="dk-ring"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={left <= 10 ? 'var(--hx-negative)' : 'var(--hx-accent)'}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - frac)}
        />
      </svg>
      <span className="hx-num absolute text-[12px] font-extrabold">{fa(left)}</span>
    </span>
  );
}

export function TypingDots({ label }: { label: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-accent-text"
      role="status"
    >
      <span className="inline-flex gap-0.5" aria-hidden>
        <span className="dk-dot size-1.5 rounded-full bg-accent" />
        <span className="dk-dot size-1.5 rounded-full bg-accent" />
        <span className="dk-dot size-1.5 rounded-full bg-accent" />
      </span>
      {label}
    </span>
  );
}

/** Initials disc tinted by conversation kind (no photo, no PII beyond the name the agent may see). */
export function WhoAvatar({
  name,
  kind,
  online,
  size = 44,
}: {
  name: string | null;
  kind: ThreadKind;
  online?: boolean;
  size?: number;
}) {
  const initials = (name ?? '').trim()[0] ?? '';
  return (
    <span
      className={cx(
        'relative grid shrink-0 place-items-center rounded-full font-extrabold',
        kind === 'TICKET'
          ? 'bg-surface-3 text-fg'
          : kind === 'CHAT_SITE'
            ? 'bg-[color-mix(in_srgb,var(--hx-info)_22%,var(--hx-surface-2))] text-fg'
            : 'bg-accent-tint text-accent-text ring-2 ring-[color-mix(in_srgb,var(--hx-accent)_45%,transparent)]',
      )}
      style={{ width: size, height: size, fontSize: size * 0.34 }}
      aria-hidden
    >
      {initials || <UserRound className="size-1/2" />}
      {online && (
        <span className="absolute -bottom-0.5 -end-0.5 size-3.5 rounded-full border-2 border-surface-1 bg-positive" />
      )}
    </span>
  );
}
